import React, { useState, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyCashFlowStatement } from "../hooks/useCompanyCashFlowStatement";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";
import { TransformedDataPoint } from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const CASH_FLOW_CATEGORIES = [
  {
    key: "freeCashFlow",
    label: "Free Cash Flow",
    shortLabel: "Free Cash Flow",
    color: "#22c55e",
    field: "freeCashFlow",
  },
  {
    key: "sbc",
    label: "Stock-Based Compensation",
    shortLabel: "Stock Based Comp",
    color: "#f59e0b",
    field: "stockBasedCompensation",
  },
] as const;

const categoryColors = CASH_FLOW_CATEGORIES.reduce(
  (acc, cat) => {
    acc[cat.key] = cat.color;
    return acc;
  },
  {} as Record<string, string>
);

interface CashFlowBreakdownChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const CashFlowBreakdownChart: React.FC<CashFlowBreakdownChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyCashFlowStatement(
    ticker,
    maxLimit,
    period
  );

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(CASH_FLOW_CATEGORIES.map((cat) => cat.key))
  );

  const chartData = useMemo((): TransformedDataPoint[] => {
    if (!data || data.length === 0) return [];

    const validData = data.filter((item) =>
      item.freeCashFlow !== null || item.stockBasedCompensation !== null
    );

    if (effectiveViewMode === "quarterly") {
      const sortedData = validData.sort(
        (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
      );

      const periodCounts = new Map<string, number>();

      const result = sortedData.map((item) => {
        const basePeriod = `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`;
        const count = periodCounts.get(basePeriod) ?? 0;
        periodCounts.set(basePeriod, count + 1);
        const periodLabel =
          count > 0 ? `${basePeriod} (${count + 1})` : basePeriod;

        return {
          period: periodLabel,
          freeCashFlow: (item.freeCashFlow ?? 0) / 1000000,
          sbc: (item.stockBasedCompensation ?? 0) / 1000000,
          date: item.date,
          year: dayjs(item.date).year(),
          quarter: dayjs(item.date).quarter(),
        };
      });

      return safeSlice(result, effectiveLimit);
    }

    const result = validData
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map((item) => ({
        period: dayjs(item.date).year().toString(),
        freeCashFlow: (item.freeCashFlow ?? 0) / 1000000,
        sbc: (item.stockBasedCompensation ?? 0) / 1000000,
        date: item.date,
        year: dayjs(item.date).year(),
        quarter: null,
      }));

    return safeSlice(result, effectiveLimit);
  }, [data, effectiveViewMode, effectiveLimit]);

  const visibleKeys = CASH_FLOW_CATEGORIES
    .filter((cat) => visibleCategories.has(cat.key))
    .map((cat) => cat.key);

  const minValue = useMemo((): number | "auto" => {
    if (chartData.length === 0) return "auto";
    const fcfValues = chartData.map((item) => item.freeCashFlow as number);
    const minFCF = Math.min(...fcfValues);
    if (minFCF >= 0) return "auto";
    const maxVal = Math.max(...fcfValues, ...chartData.map((item) => item.sbc as number));
    const range = maxVal - minFCF;
    const padding = range * 0.1;
    return minFCF - padding;
  }, [chartData]);

  const toggleCategory = (categoryKey: string) => {
    setVisibleCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const chipLegend = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: noHover ? 0 : 2,
        justifyContent: "center",
        px: noHover ? 0 : 2,
        ...(noHover && {
          maxWidth: "750px",
          mx: "auto",
        }),
      }}
    >
      {CASH_FLOW_CATEGORIES.map((category) => (
        <ChartToggleButton
          key={category.key}
          label={category.shortLabel}
          selected={visibleCategories.has(category.key)}
          onToggle={() => toggleCategory(category.key)}
          color={category.color}
        />
      ))}
    </Box>
  );

  return (
    <GenericFinancialBarChart
      title="Cash Flow Breakdown"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={visibleKeys}
      groupMode="grouped"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors={({ id, data }: any) => {
        if (id === "freeCashFlow") {
          return (data.freeCashFlow ?? 0) >= 0 ? "#22c55e" : "#ef4444";
        }
        return categoryColors[id as string] ?? "#f59e0b";
      }}
      minValue={minValue}
      yAxisLegend="Cash Flow"
      yAxisFormat={(value) => {
        const absValue = Math.abs(value);
        const formattedValue = formatNumber(absValue * 1000000);
        return value < 0
          ? `-${currencySymbol}${formattedValue}`
          : `${currencySymbol}${formattedValue}`;
      }}
      valueLabelsFormat={(value) => {
        const absValue = Math.abs(value);
        const formattedValue = formatNumber(absValue * 1000000, 0);
        return value < 0
          ? `-${currencySymbol}${formattedValue}`
          : `${currencySymbol}${formattedValue}`;
      }}
      noDataTitle="No Cash Flow Data Available"
      noDataDescription="Cash flow data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
      tooltipContent={({ id, value, indexValue, color }) => {
        const category = CASH_FLOW_CATEGORIES.find((cat) => cat.key === id);
        const v = value as number;
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: category?.label ?? "", value: `${v >= 0 ? currencySymbol : `-${currencySymbol}`}${formatNumber(Math.abs(v) * 1000000, 2)}` }]}
          />
        );
      }}
      belowChartContent={chipLegend}
    />
  );
};

export default CashFlowBreakdownChart;
